// Dense, scannable contacts table. Master in the master/detail layout.
import type { Contact, Density } from "../types/inpact";
import { CertificationChip } from "./CertificationChip";
import { StatusBadge } from "./StatusBadge";
import { SalesforceDeepLink } from "./SalesforceDeepLink";
import { PaginationFooter } from "./ResultsTable/PaginationFooter";
import { ExportCsvButton } from "./ResultsTable/ExportCsvButton";
import { useExplorerStore } from "../store/explorerStore";
import { displayCountry, flagFor } from "../lib/countryUtils";

interface ResultsTableProps {
  // Already-paginated rows for the current page (offset slice done by
  // backend; useContacts forwards them).
  rows: Contact[];
  total: number;
  totalPages: number;
  loading: boolean;
}

export function ResultsTable({ rows, total, totalPages, loading }: ResultsTableProps) {
  const {
    page,
    pageSize,
    density,
    selectedContactId,
    setPage,
    setPageSize,
    selectContact,
    setDensity,
  } = useExplorerStore();

  const pagedRows = rows;

  return (
    <div className="table-card">
      <div className="table-toolbar">
        <div className="table-toolbar__title">
          Results <small>{loading ? "…" : `${total} contacts`}</small>
        </div>
        <div className="table-toolbar__actions">
          <DensityToggle density={density} onChange={setDensity} />
          <ExportCsvButton />
          {/* Column selection remains a possible future enhancement. */}
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="results-table" data-density={density} aria-label="INPACT contacts">
          <thead>
            <tr>
              <th aria-label="Status" style={{ width: 130 }}>Status</th>
              <th>Name</th>
              <th style={{ width: 160 }}>Country</th>
              <th style={{ width: 200 }}>Account</th>
              <th>Certifications</th>
              <th style={{ width: 140 }}>Certified</th>
              <th aria-label="Open in Salesforce" style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((c) => (
              <ResultRow
                key={c.id}
                contact={c}
                selected={c.id === selectedContactId}
                onSelect={() => selectContact(c.id)}
                density={density}
              />
            ))}
            {!loading && pagedRows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: "var(--space-12)", textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "var(--font-size-lg)",
                      fontWeight: "var(--font-weight-semibold)",
                      color: "var(--color-neutral-900)",
                    }}
                  >
                    No contacts match these filters
                  </div>
                  <div
                    style={{
                      fontSize: "var(--font-size-sm)",
                      color: "var(--color-neutral-600)",
                      marginTop: "var(--space-2)",
                    }}
                  >
                    Try widening one of your filters or clearing the search.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationFooter
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}

interface ResultRowProps {
  contact: Contact;
  selected: boolean;
  onSelect: () => void;
  density: Density;
}

function ResultRow({ contact, selected, onSelect, density }: ResultRowProps) {
  const certs = contact.certifications.slice(0, 2);
  const more = contact.certifications.length - certs.length;
  return (
    <tr
      className={selected ? "is-selected" : undefined}
      role="row"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <td>
        <StatusBadge status={contact.associateStatus} inactive={contact.inactive} />
      </td>
      <td>
        <div className="cell-name__primary">
          {contact.firstName} {contact.lastName}
        </div>
        {contact.email && <span className="cell-name__email">{contact.email}</span>}
      </td>
      <td>
        {flagFor(contact.countryOfResidence)} {displayCountry(contact.countryOfResidence)}
      </td>
      <td>
        {contact.account ? (
          <a className="cell-account" href="#" onClick={(e) => e.preventDefault()}>
            {contact.account.name}
          </a>
        ) : (
          <span style={{ color: "var(--color-neutral-400)" }}>—</span>
        )}
      </td>
      <td>
        <div className="cell-certs">
          {certs.map((c, i) => (
            <CertificationChip key={i} cert={c} density={density} />
          ))}
          {more > 0 && (
            <span className="cert-chip cert-chip--more">+{more} more</span>
          )}
          {contact.certifications.length === 0 && (
            <span style={{ color: "var(--color-neutral-400)", fontSize: "var(--font-size-xs)" }}>—</span>
          )}
        </div>
      </td>
      <td className="cell-date" title={contact.dateOfApproval ?? ""}>
        {contact.dateOfApproval ?? "—"}
      </td>
      <td>
        {contact.id && (
          <SalesforceDeepLink
            sfId={contact.id}
            objectType="Contact"
            label={`Open ${contact.firstName} ${contact.lastName} in Salesforce (new tab)`}
          />
        )}
      </td>
    </tr>
  );
}

function DensityToggle({
  density,
  onChange,
}: {
  density: Density;
  onChange: (d: Density) => void;
}) {
  return (
    <div className="density-toggle" role="group" aria-label="Row density">
      <button
        type="button"
        aria-pressed={density === "comfortable"}
        title="Comfortable density"
        onClick={() => onChange("comfortable")}
      >
        ≡
      </button>
      <button
        type="button"
        aria-pressed={density === "compact"}
        title="Compact density"
        onClick={() => onChange("compact")}
      >
        ≣
      </button>
    </div>
  );
}
